import React, { useState, useEffect } from 'react';
import { recommendationsService } from '../services/recommendationsService';
import { eventsService } from '../services/eventsService';
import { WhyTheseResults } from '../components/personalization/WhyTheseResults';

/**
 * RecommendationsPage - Page showing personalized recommendations
 */
export function RecommendationsPage() {
  const [recommendations, setRecommendations] = useState({
    forYou: [],
    becauseYouLiked: [],
    continueExploring: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await recommendationsService.getRecommendations();
      setRecommendations(data);

      // Track impression
      eventsService.trackImpression(null, 'recommendations', [], []);
    } catch (err) {
      console.error('Failed to load recommendations', err);
      setError('Failed to load recommendations. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemClick = (item, section) => {
    eventsService.trackClick(
      item.id,
      `recommendations:${section}`,
      item.primaryTags || item.auto_tags || [],
      item.primaryColor ? [item.primaryColor] : []
    );
  };

  const renderSection = (title, items, reason, sectionKey) => {
    if (!items || items.length === 0) {
      return null;
    }

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">{title}</h2>
        {reason && (
          <p className="text-gray-600 mb-4">{reason}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              onClick={() => handleItemClick(item, sectionKey)}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
            >
              {item.url && (
                <img
                  src={item.url}
                  alt={item.title || 'Item'}
                  className="w-full h-48 object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2 truncate">
                  {item.title || 'Untitled'}
                </h3>
                {item.primaryTags && item.primaryTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {item.primaryTags.slice(0, 3).map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {item.primaryColor && (
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: item.primaryColor }}
                    />
                    <span className="text-xs text-gray-600">{item.primaryColor}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-64 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadRecommendations}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Recommendations</h1>
        <p className="text-gray-600">
          Personalized content based on your interests and activity
        </p>
      </div>

      {renderSection(
        'For You',
        recommendations.forYou,
        'Based on your preferences and past interactions',
        'forYou'
      )}

      {renderSection(
        'Because You Liked',
        recommendations.becauseYouLiked,
        'Similar items to ones you\'ve saved or liked',
        'becauseYouLiked'
      )}

      {renderSection(
        'Continue Exploring',
        recommendations.continueExploring,
        'From your saved searches',
        'continueExploring'
      )}

      {recommendations.forYou.length === 0 &&
        recommendations.becauseYouLiked.length === 0 &&
        recommendations.continueExploring.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              No recommendations yet. Start exploring and saving items to get personalized suggestions!
            </p>
          </div>
        )}
    </div>
  );
}

